// BHP-1063: DNS-record writes over Backend2.0.
//
// Every write is synced to Cloudflare by the backend inside the request
// (BHP-1011): a 4xx from Cloudflare comes back as 422 and nothing is stored, a
// transient failure stores the record as `pending` and is retried
// asynchronously. So a 2xx does not always mean "live at Cloudflare" — the
// caller should show `syncStatus`.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../../../shared/http/httpClient';
import { normalizeItem } from '../../../shared/api/hydra';
import type { DnsRecord, DnsRecordInput } from '../dns';

/** Everything DNS-record-shaped in the cache, regardless of filter or page. */
const RECORDS_KEY = ['hydra', 'dns_records'];

interface RecordPayload {
  type: string;
  name: string;
  value: string;
  priority: string;
  ttl: number;
  dnsZoneUuid?: string;
}

function toPayload(input: DnsRecordInput, dnsZoneUuid?: string): RecordPayload {
  return {
    type: input.type,
    name: input.name.trim(),
    value: input.value.trim(),
    // The API takes priority as a string; non-MX/SRV types carry '0'.
    priority: input.priority.trim() === '' ? '0' : input.priority.trim(),
    ttl: input.ttl,
    ...(dnsZoneUuid !== undefined ? { dnsZoneUuid } : {})
  };
}

export function useCreateDnsRecord(zoneUuid: string): {
  create: (input: DnsRecordInput) => Promise<DnsRecord>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: DnsRecordInput): Promise<DnsRecord> => {
      const raw = await httpClient.post<Record<string, unknown>>(
        '/dns_records',
        toPayload(input, zoneUuid)
      );
      return normalizeItem<DnsRecord>(raw);
    },
    // Returning the promise keeps the mutation pending until the list has
    // actually refetched. Fire-and-forget left a window where the row was still
    // on screen but its buttons were live again — long enough to double-submit.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: RECORDS_KEY });
    }
  });

  return { create: mutation.mutateAsync, isPending: mutation.isPending };
}

export function useUpdateDnsRecord(): {
  update: (args: { uuid: string; input: DnsRecordInput }) => Promise<DnsRecord>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      uuid,
      input
    }: {
      uuid: string;
      input: DnsRecordInput;
    }): Promise<DnsRecord> => {
      // PATCH is application/merge-patch+json — httpClient sets that itself.
      const raw = await httpClient.patch<Record<string, unknown>>(
        `/dns_records/${uuid}`,
        toPayload(input)
      );
      return normalizeItem<DnsRecord>(raw);
    },
    // Returning the promise keeps the mutation pending until the list has
    // actually refetched. Fire-and-forget left a window where the row was still
    // on screen but its buttons were live again — long enough to double-submit.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: RECORDS_KEY });
    }
  });

  return { update: mutation.mutateAsync, isPending: mutation.isPending };
}

export function useDeleteDnsRecord(): {
  remove: (uuid: string) => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (uuid: string): Promise<void> => {
      await httpClient.delete(`/dns_records/${uuid}`);
    },
    // Returning the promise keeps the mutation pending until the list has
    // actually refetched. Fire-and-forget left a window where the row was still
    // on screen but its buttons were live again — long enough to double-submit.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: RECORDS_KEY });
    }
  });

  return { remove: mutation.mutateAsync, isPending: mutation.isPending };
}
